import axios from 'axios';

export const ONBOARDING_STEP_IDS = {
  welcome: 'welcome',
  profileSnapshot: 'profile-snapshot',
  interview: 'interview',
  quiz: 'quiz',
  preferences: 'preferences',
  events: 'events-interests',
  dinnerAvailability: 'dinner-availability',
  complete: 'complete',
};

const MEMBER_PORTRAIT_UNLOCK_STATUS_KEYS = ['interview', 'dinnerAvailability'];

const REQUIRED_RATED_PREFERENCES = 5;

export const countActivePreferences = (preferences = []) =>
  preferences.filter((preference) => preference.rating && preference.rating !== 'unsure').length;

export const countRatedPreferencesFromCategories = (categories = []) =>
  categories.reduce(
    (count, category) =>
      count +
      (category.subcategories || []).filter(
        (subcategory) => subcategory.rating && subcategory.rating !== 'unsure'
      ).length,
    0
  );

/** Chief interview done: API returned a trajectory, not only a quiz-created shell row. */
export const hasDossier = (dossier) =>
  Boolean(
    (dossier?.candidateName || '').trim() &&
    (dossier?.shadowResume?.trajectory || '').trim()
  );

export const hasQuizProfile = (profile, dossier) =>
  Boolean(
    (profile && typeof profile.openness === 'number') ||
      dossier?.personalitySummary ||
      dossier?.personalityScores
  );

export const hasMeaningfulEventsInterests = (eventsInterests) =>
  Boolean(
    (eventsInterests?.dietary_restrictions || []).length > 0 ||
      (eventsInterests?.beverage_preferences || []).length > 0 ||
      Object.keys(eventsInterests?.off_the_clock_interests || {}).length > 0 ||
      (eventsInterests?.room_conflicts || '').trim() !== ''
  );

export const hasProfileSnapshot = (user) =>
  Boolean((user?.title || '').trim() && (user?.pronouns || '').trim());

export const hasDinnerAvailability = (dinnerPreferences) =>
  Boolean(
    (dinnerPreferences?.preferred_slots || []).length > 0 ||
      (dinnerPreferences?.preferred_cities || []).length > 0
  );

export const deriveOnboardingCompletion = ({
  user,
  dossier,
  personalityProfile,
  preferences,
  eventsInterests,
  dinnerPreferences,
}) => {
  const activePreferencesCount = countActivePreferences(preferences);
  const status = {
    profileSnapshot: hasProfileSnapshot(user),
    interview: hasDossier(dossier),
    quiz: hasQuizProfile(personalityProfile, dossier),
    preferences: activePreferencesCount >= REQUIRED_RATED_PREFERENCES,
    events: hasMeaningfulEventsInterests(eventsInterests),
    dinnerAvailability: hasDinnerAvailability(dinnerPreferences),
  };

  return {
    status,
    activePreferencesCount,
    hasMeaningfulEventsInterests: status.events,
  };
};

export const getFirstIncompleteOnboardingStep = (completion) => {
  const status = completion?.status || {};
  const interviewOk = !!status.interview;
  const dinnerOk = !!status.dinnerAvailability;
  const interviewDeferred = !!completion?.interviewDeferred;

  if (interviewOk && dinnerOk) return ONBOARDING_STEP_IDS.complete;
  if (interviewOk && !dinnerOk) return ONBOARDING_STEP_IDS.dinnerAvailability;
  if (!interviewOk && interviewDeferred) {
    if (!dinnerOk) return ONBOARDING_STEP_IDS.dinnerAvailability;
    return ONBOARDING_STEP_IDS.interview;
  }
  if (completion?.resumeStepId) return completion.resumeStepId;
  if (!interviewOk) return ONBOARDING_STEP_IDS.welcome;
  if (!dinnerOk) return ONBOARDING_STEP_IDS.dinnerAvailability;
  return ONBOARDING_STEP_IDS.complete;
};

export const getMemberPortraitUnlockProgress = (completion = {}) => {
  const status = completion?.status || {};
  const totalCount = MEMBER_PORTRAIT_UNLOCK_STATUS_KEYS.length;
  const completedCount = MEMBER_PORTRAIT_UNLOCK_STATUS_KEYS.filter((key) => Boolean(status[key])).length;
  const remainingCount = Math.max(totalCount - completedCount, 0);

  return {
    totalCount,
    completedCount,
    remainingCount,
    percent: totalCount ? Math.round((completedCount / totalCount) * 100) : 0,
  };
};

export const fetchOnboardingBootstrap = async (token, { cachedUser } = {}) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [
    meRes,
    completionRes,
    dossierRes,
    quizRes,
    preferencesRes,
    preferenceCategoriesRes,
    eventsInterestsRes,
    offTheClockTopicsRes,
    dinnerPreferencesRes,
  ] = await Promise.all([
    cachedUser ? Promise.resolve(null) : axios.get('/api/auth/me', { headers }),
    axios.get('/api/onboarding/completion', { headers }).catch(() => null),
    axios.get('/api/concierge/dossier', { headers }).catch(() => null),
    axios.get('/api/personality/profile', { headers }).catch(() => null),
    axios.get('/api/preferences', { headers }).catch(() => null),
    axios.get('/api/preferences/categories', { headers }).catch(() => null),
    axios.get('/api/events-interests', { headers }).catch(() => null),
    axios.get('/api/off-the-clock-topics', { headers }).catch(() => null),
    axios.get('/api/dinner-preferences', { headers }).catch(() => null),
  ]);

  const user = cachedUser || meRes?.data?.user || meRes?.data?.data || null;
  const dossier = dossierRes?.data?.data?.dossier || dossierRes?.data?.dossier || null;
  const personalityProfile = quizRes?.data?.profile || null;
  const preferences = preferencesRes?.data?.preferences || preferencesRes?.data?.data?.preferences || [];
  const preferenceCategories = preferenceCategoriesRes ? (preferenceCategoriesRes.data?.categories || []) : null;
  const eventsInterests = eventsInterestsRes ? (eventsInterestsRes.data?.data || {}) : null;
  const offTheClockTopics = offTheClockTopicsRes ? (offTheClockTopicsRes.data?.data || {}) : null;
  const dinnerPreferences = dinnerPreferencesRes?.data?.data?.preferences || null;
  const completionPayload = completionRes?.data?.data || completionRes?.data || {};

  const derivedCompletion = deriveOnboardingCompletion({
    user,
    dossier,
    personalityProfile,
    preferences,
    eventsInterests: eventsInterests || {},
    dinnerPreferences: dinnerPreferences || {},
  });

  return {
    user,
    dossier,
    personalityProfile,
    preferences,
    preferenceCategories,
    eventsInterests,
    offTheClockTopics,
    dinnerPreferences,
    completion: mergeOnboardingCompletionFromApi(derivedCompletion, completionPayload),
  };
};

/**
 * Merge client-derived onboarding flags with GET /api/onboarding/completion.
 * Prefer server `status.dinnerAvailability` when boolean so UI matches `waitlist_handoff_complete`.
 */
export function mergeOnboardingCompletionFromApi(derivedCompletion, completionPayload = {}) {
  const apiStatus = completionPayload.status || {};
  const dinnerAvailability =
    typeof apiStatus.dinnerAvailability === 'boolean'
      ? apiStatus.dinnerAvailability
      : derivedCompletion.status.dinnerAvailability;
  const hasDinnerPreferences =
    typeof completionPayload.has_dinner_preferences === 'boolean'
      ? completionPayload.has_dinner_preferences
      : dinnerAvailability;

  return {
    ...derivedCompletion,
    status: {
      ...derivedCompletion.status,
      ...apiStatus,
      dinnerAvailability,
    },
    complete: completionPayload.complete ?? false,
    waitlistHandoffComplete: completionPayload.waitlist_handoff_complete ?? false,
    interviewDeferred: !!completionPayload.interview_deferred,
    resumeStepId: completionPayload.resume_step_id || null,
    hasDinnerPreferences,
  };
}

/* ── Bootstrap prefetch cache ──
 * Allows AuthContext to start the bootstrap fetch early so WelcomeOnboarding
 * can consume the already-in-flight (or resolved) promise instead of fetching again. */

let _bootstrapPromise = null;
let _bootstrapToken = null;

export const prefetchOnboardingBootstrap = (token, options = {}) => {
  if (_bootstrapPromise && _bootstrapToken === token) {
    return _bootstrapPromise;
  }
  _bootstrapToken = token;
  _bootstrapPromise = fetchOnboardingBootstrap(token, options).catch((err) => {
    _bootstrapPromise = null;
    _bootstrapToken = null;
    throw err;
  });
  return _bootstrapPromise;
};

export const consumeOnboardingBootstrap = () => {
  const promise = _bootstrapPromise;
  _bootstrapPromise = null;
  _bootstrapToken = null;
  return promise;
};

export const clearBootstrapCache = () => {
  _bootstrapPromise = null;
  _bootstrapToken = null;
};
